import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { keycloakConfig, decodeJwt, TOKEN_REFRESH_THRESHOLD, MAX_RETRY_DELAY } from '@/features/auth/auth.config';
import type { JwtUser } from '@/features/auth/auth.config';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  user: JwtUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<boolean>;
  getAccessToken: () => string | null;
  clearError: () => void;
}

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let retryCount = 0;

function scheduleRefresh(expiresIn: number, refreshFn: () => Promise<boolean>) {
  if (refreshTimer) clearTimeout(refreshTimer);
  const delay = expiresIn * TOKEN_REFRESH_THRESHOLD * 1000;
  refreshTimer = setTimeout(async () => {
    const success = await refreshFn();
    if (!success) {
      // Exponential backoff
      retryCount++;
      const retryDelay = Math.min(1000 * Math.pow(2, retryCount), MAX_RETRY_DELAY);
      refreshTimer = setTimeout(() => refreshFn(), retryDelay);
    } else {
      retryCount = 0;
    }
  }, delay);
}

const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(keycloakConfig.tokenUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
              grant_type: 'password',
              client_id: keycloakConfig.clientId,
              username: email,
              password,
            }),
          });

          if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            const errorMsg = data.error_description || data.error || 'Identifiants invalides';
            set({ isLoading: false, error: errorMsg });
            return;
          }

          const data = await response.json();
          const user = decodeJwt(data.access_token);
          const expiresAt = Date.now() + data.expires_in * 1000;

          set({
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
            expiresAt,
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          scheduleRefresh(data.expires_in, get().refreshAuth);
        } catch (err) {
          set({
            isLoading: false,
            error: navigator.onLine
              ? 'Erreur de connexion au serveur'
              : 'Connexion impossible hors ligne',
          });
        }
      },

      logout: () => {
        if (refreshTimer) clearTimeout(refreshTimer);
        retryCount = 0;

        const rt = get().refreshToken;
        // Fire and forget Keycloak logout
        if (rt) {
          fetch(keycloakConfig.logoutUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
              client_id: keycloakConfig.clientId,
              refresh_token: rt,
            }),
          }).catch(() => {});
        }

        set({
          accessToken: null,
          refreshToken: null,
          expiresAt: null,
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },

      refreshAuth: async () => {
        const rt = get().refreshToken;
        if (!rt) return false;

        try {
          const response = await fetch(keycloakConfig.tokenUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
              grant_type: 'refresh_token',
              client_id: keycloakConfig.clientId,
              refresh_token: rt,
            }),
          });

          if (!response.ok) {
            // Refresh token expired — don't force logout if offline
            if (navigator.onLine) {
              get().logout();
            }
            return false;
          }

          const data = await response.json();
          const user = decodeJwt(data.access_token);
          const expiresAt = Date.now() + data.expires_in * 1000;

          set({
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
            expiresAt,
            user,
            isAuthenticated: true,
          });

          scheduleRefresh(data.expires_in, get().refreshAuth);
          return true;
        } catch {
          // Network error — keep current session, retry later
          return false;
        }
      },

      getAccessToken: () => get().accessToken,

      clearError: () => set({ error: null }),
    }),
    {
      name: 'goflux-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        expiresAt: state.expiresAt,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      // After page reload Zustand rehydrates from localStorage but no refresh
      // timer is scheduled, so the token silently expires and every API call
      // fails with 401. Re-arm the timer here, and if the token already expired
      // while the tab was closed, refresh immediately.
      onRehydrateStorage: () => (state) => {
        if (!state || !state.refreshToken || !state.expiresAt) return;
        const remainingMs = state.expiresAt - Date.now();
        if (remainingMs <= 0) {
          state.refreshAuth();
          return;
        }
        const expiresInSeconds = Math.floor(remainingMs / 1000);
        scheduleRefresh(expiresInSeconds, state.refreshAuth);
      },
    }
  )
);

export default useAuthStore;
